import { FGCustomPanel, FGListen, FGSelectInput, FieldSet } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { BlockedHealthTestDto, DogApi, ELab, EPlatform, ERequestState, HealthTestRequestApi } from "../../../../api";
import { useAuth } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { HealthPacksList } from "../../../../pages/request/healthTestRequest/item/HealthPacksList";
import { HealthTestList } from "../../../../pages/request/healthTestRequest/item/HealthTestList";
import { DrupalStepperButtonBloc } from "../../../common";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";

export interface IDrupalHealthTestFormProps {
  healthTestRequestId: number;
  stepNumber: number;
  initialdata: any;
  onStepChange: (newStep: number) => void;
  save: (data?: any) => Promise<any> | any;
  loading: boolean;
  showStepper?: boolean;
}

export const DrupalHealthTestForm: React.FunctionComponent<IDrupalHealthTestFormProps> = ({
  healthTestRequestId,
  onStepChange,
  stepNumber,
  save,
  loading,
  initialdata,
  showStepper = true,
}) => {
  const { user } = useAuth();
  const { t } = useTl();
  const api = useApiService(HealthTestRequestApi);
  const dogApi = useApiService(DogApi);
  const [currentStep, setCurrentStep] = React.useState(stepNumber);
  const [dogId, setDogId] = React.useState();
  const [selectedLab, setSelectedLab] = React.useState<ELab>(null);
  const saveForm = React.useCallback(
    async (formikData) => {
      if (currentStep === 1) {
        await save(formikData);
      }
      window.scrollTo(0, 0);
    },
    [currentStep, save]
  );

  const [dogs, dloading] = useReferential(
    (a) => a.getOwnedDogs(user.userId, initialdata?.dogId ? initialdata.dogId : 0, null, null, true),
    true,
    []
  );

  const [blockedIds, setBlockedIds] = React.useState<BlockedHealthTestDto>(null);
  const getBlockedIds = React.useCallback(
    async (blockedIdsDogId: any) => {
      var result = await api.getBlockedHealthTests(healthTestRequestId, blockedIdsDogId);
      setBlockedIds(result);
    },
    [api, healthTestRequestId]
  );

  const formSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("analysis-selected", t(ETLCodes.SelectAtLeastOneAnalysisOrPack), async function (value) {
          if (value) {
            return this.parent.healthPackIds?.length > 0 || this.parent.singleHealthTestIds?.length > 0;
          }
          return true;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (value) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (value) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
    });
  }, [dogApi, t]);

  return (
    <DrupalFormGenerator
      initialValues={initialdata}
      editMode={currentStep === 0 && initialdata.request.status === ERequestState.REQUESTED}
      onSubmit={() => {}}
      saving={false}
      validationSchema={formSchema}
      loading={loading}
      hideButtons
    >
      {currentStep !== 0 ? <DrupalFieldSet title={t(ETLCodes.RequestResume)} /> : <></>}
      <FGSelectInput
        name="dogId"
        label={t(ETLCodes.Dog)}
        items={dogs}
        loading={dloading}
        readonly={initialdata?.healthTestRequestId >= 1}
      />
      <FGListen
        field="dogId"
        onChanged={(value, formik) => {
          setDogId(value);
          if (value && formik?.values?.dogId) {
            getBlockedIds(formik?.values?.dogId);
          }
        }}
      />
      <DrupalFieldSet title={t(ETLCodes.RequestData)}>
        <FieldSet title={t(ETLCodes.HealthTests)}>
          <FGCustomPanel>
            {({ formik }) => (
              <HealthTestList
                onSelectionUpdated={(value) => {
                  formik.setFieldValue("singleHealthTestIds", value);
                  if (!value || value?.length === 0) {
                    setSelectedLab(null);
                  } else {
                    setSelectedLab(ELab.PROGENUS);
                  }
                }}
                lab={ELab.PROGENUS}
                selectedIds={formik?.values?.singleHealthTestIds ? [...formik?.values?.singleHealthTestIds] : []}
                blockedIds={blockedIds?.blockedSingleHealthTestsIds}
                disabled={
                  currentStep !== 0 ||
                  initialdata.request.status !== ERequestState.REQUESTED ||
                  (selectedLab !== null && selectedLab !== ELab.PROGENUS)
                }
                healthTestRequestId={null}
                platform={EPlatform.PORTAL}
              />
            )}
          </FGCustomPanel>
        </FieldSet>

        <FieldSet title={t(ETLCodes.HealthPack)}>
          <FGCustomPanel>
            {(ctx) => (
              <HealthPacksList
                selectedIds={ctx.formik?.values?.healthPackIds ? [...ctx.formik.values.healthPackIds] : []}
                lab={ELab.PROGENUS}
                dogId={dogId ?? null}
                onSelectionUpdated={(value) => {
                  ctx.formik.setFieldValue("healthPackIds", value);
                  if (!value || value?.length === 0) {
                    setSelectedLab(null);
                  } else {
                    setSelectedLab(ELab.PROGENUS);
                  }
                }}
                blockedIds={blockedIds?.blockedHealthTestPackIds}
                healthTestRequestId={null}
                disabled={
                  currentStep !== 0 ||
                  initialdata.request.status !== ERequestState.REQUESTED ||
                  (selectedLab !== null && selectedLab !== ELab.PROGENUS)
                }
              />
            )}
          </FGCustomPanel>
        </FieldSet>
      </DrupalFieldSet>
      {showStepper ? (
        <FGCustomPanel>
          {(ctx) => {
            return (
              <DrupalStepperButtonBloc
                backOnly={initialdata.request.status !== ERequestState.REQUESTED}
                disabled={ctx.formik.values === initialdata || !ctx.formik.isValid}
                loading={loading}
                onStepChange={(newStep) => {
                  setCurrentStep(newStep);
                  onStepChange(newStep);
                }}
                stepNumber={currentStep}
                onNextFn={() => saveForm(ctx.formik.values)}
                formik={ctx.formik}
              />
            );
          }}
        </FGCustomPanel>
      ) : null}
    </DrupalFormGenerator>
  );
};
